// M1 throwaway: FE-owned chat lifecycle for flow (Live) analytics.
//
// Domain-specific provisioning (workspace schema snapshot, flow-scoped system
// prompt, special_domains tool config) lives here. The shared session state
// machine is in useAnalyticsChatSession.

import { useCallback, useMemo } from "react";

import { v4 as uuidv4 } from "uuid";

import {
  type AnalyticsChatResult,
  useAnalyticsChatSession,
} from "src/analytics/copilot/useAnalyticsChatSession";
import {
  LIVE_SPECIAL_DOMAINS,
  specialDomainsForAgent,
} from "src/analytics/metrics/VisualizeMetrics/liveAnalyticsDomains";
import { agentManagementApi } from "src/api/endpoints";
import { useCurrentWorkspace, useFlow } from "src/api/queries";
import {
  type AgentVersionConfigurationInputToolsInner,
  AgentVersionConfigurationInputToolsInnerTypeEnum,
  Model,
} from "src/clients/agent-management";
import { buildLiveAnalyticsSystemPrompt } from "src/flow/analytics/m1/liveAnalyticsSystemPrompt";
import {
  QUERY_METRICS_TOOL_DESCRIPTION,
  REPLY_VISUALIZE_METRICS_TOOL_DESCRIPTION,
} from "src/flow/analytics/m1/liveAnalyticsToolDescriptions";
import { fetchPreflightBlocks } from "src/flow/analytics/m1/workspaceSchemaSnapshot";
import { useEnvironment } from "src/hooks/useEnvironment";

const SLUG_PREFIX = "live-analytics-m1-";
const AGENT_NAME = "Live Analytics (M1)";
const AGENT_DESCRIPTION =
  "Auto-provisioned per-session Live Analytics chat agent (OPTI-6761 throwaway)";

// Keep the session resumable for 7 days; the backend default (24h) expires it
// too soon, so a chat can't be continued the next day.
const SESSION_MAX_LIFETIME_SECONDS = 7 * 24 * 60 * 60;
// Surface expiry on the FE a day before the backend hard limit so the user gets
// a graceful "start a new chat" message instead of a failed invoke at the edge.
const SESSION_EXPIRY_THRESHOLD_MS = 6 * 24 * 60 * 60 * 1000;
const SESSION_EXPIRED_MESSAGE =
  "This chat has expired. Start a new conversation to continue.";

const SESSIONS_STORAGE_KEY = "liveAnalyticsM1Sessions";

// `flow_slug` etc. are only enforced for FLOW tools; cast through `unknown`
// to satisfy the union TS type. Same pattern as `useCaseCopilotInvoke`.
const buildTools = (): AgentVersionConfigurationInputToolsInner[] =>
  [
    {
      id: uuidv4(),
      type: AgentVersionConfigurationInputToolsInnerTypeEnum.QUERY_METRICS,
      name: "query_metrics",
      description: QUERY_METRICS_TOOL_DESCRIPTION,
    },
    {
      id: uuidv4(),
      type: AgentVersionConfigurationInputToolsInnerTypeEnum.REPLY_VISUALIZE_METRICS,
      name: "reply_visualize_metrics",
      description: REPLY_VISUALIZE_METRICS_TOOL_DESCRIPTION,
      chart_format: "vega",
      special_domains: specialDomainsForAgent(LIVE_SPECIAL_DOMAINS),
    },
  ] as unknown as AgentVersionConfigurationInputToolsInner[];

const provisionAgent = async (
  baseUrl: string,
  flowId: string,
  flowName: string,
  flowVersionIdToName: Record<string, string>,
  workspacePromptPrefix: string | undefined,
): Promise<string> => {
  const mgmt = agentManagementApi(baseUrl);

  const { workspaceSchemaBlock, flowVersionsBlock } =
    await fetchPreflightBlocks(baseUrl, flowId, flowVersionIdToName).catch(
      (err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        return {
          workspaceSchemaBlock: `_(schema snapshot unavailable — ${msg}; agent will discover keys on demand)_`,
          // Names still resolve on the chart via the label dictionary.
          flowVersionsBlock: "_(flow-version list unavailable)_",
        };
      },
    );

  const systemPrompt = buildLiveAnalyticsSystemPrompt({
    flowId,
    flowName,
    workspaceSchemaBlock,
    flowVersionsBlock,
    workspacePromptPrefix,
  });

  const slug = SLUG_PREFIX + uuidv4().slice(0, 8);
  const createResp = await mgmt.createAgentCopilotsManagementApiV0AgentsPost({
    agentCreate: {
      slug,
      name: AGENT_NAME,
      description: AGENT_DESCRIPTION,
    },
  });
  const agentId = createResp.data.agent.id;

  const versionResp =
    await mgmt.createVersionCopilotsManagementApiV0AgentsAgentIdVersionsPost({
      agentId,
      agentVersionCreate: {
        name: "v1",
        description: "Auto-provisioned M1 Live Analytics version",
        configuration: {
          model_config: {
            model: Model.CLAUDE_4_5_SONNET,
            system_prompt: systemPrompt,
            temperature: 0,
          },
          tools: buildTools(),
          execution_config: {
            session_max_lifetime_seconds: SESSION_MAX_LIFETIME_SECONDS,
          },
        },
      },
    });
  const versionId = versionResp.data.agent_version.id;

  await mgmt.publishVersionCopilotsManagementApiV0AgentsAgentIdVersionsVersionIdPublishPost(
    {
      agentId,
      versionId,
      ifMatch: "0",
    },
  );

  return agentId;
};

export const useLiveAnalyticsChat = ({
  flowId,
  flowName,
}: {
  flowId: string;
  flowName: string;
}): AnalyticsChatResult => {
  // Same source the chart's UUID→name relabeling uses; archived versions are
  // included because recent decisions can reference them.
  const flowQuery = useFlow(flowId, { includeArchivedFlowVersions: true });
  // Names are truncated to bound prompt size.
  const flowVersionIdToName = useMemo(
    () =>
      Object.fromEntries(
        (flowQuery.data?.versions ?? []).map(
          (v) => [v.id, v.name.slice(0, 40)] as const,
        ),
      ),
    [flowQuery.data?.versions],
  );
  // Gate provisioning until the flow query settles so the baked prompt has
  // version names. Errors still release the gate — names just fall back to UUIDs.
  const flowVersionsResolved = flowQuery.isSuccess || flowQuery.isError;

  // Workspace-level prompt prefix from the workspace config, prepended to the
  // system prompt when no localStorage override is set. Comes from the same
  // workspace object that gates provisioning on baseUrl, so it's loaded before
  // the agent is provisioned.
  const { data: workspace } = useCurrentWorkspace();
  const workspacePromptPrefix =
    workspace?.config?.live_analytics_system_prompt_prefix;
  const [environment] = useEnvironment();

  const provision = useCallback(
    (baseUrl: string) =>
      provisionAgent(
        baseUrl,
        flowId,
        flowName,
        flowVersionIdToName,
        workspacePromptPrefix,
      ),
    [flowId, flowName, flowVersionIdToName, workspacePromptPrefix],
  );

  return useAnalyticsChatSession({
    storageKey: SESSIONS_STORAGE_KEY,
    sessionSlotKey: flowId,
    environment,
    provision,
    provisionReady: flowVersionsResolved,
    sessionExpiryThresholdMs: SESSION_EXPIRY_THRESHOLD_MS,
    sessionExpiredMessage: SESSION_EXPIRED_MESSAGE,
  });
};
